import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaCar } from 'react-icons/fa';
import axios from 'axios';

const BilEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stationer, setStationer] = useState([]);
  const [bil, setBil] = useState(null);
  const [formData, setFormData] = useState({
    internt_nummer: '',
    nummerplade: '',
    model: '',
    vogn_type: '',
    status: 'I drift',
    station_id: '',
    type: 'Almindelig',
    boat_model: '',
    boat_motor: '',
    boat_motor_code: '',
    boat_notes: ''
  });

  useEffect(() => {
    fetchBilData();
    fetchStationer();
  }, [id]);

  const fetchBilData = async () => {
    try {
      const response = await axios.get(`/api/biler/${id}`);
      const bilData = response.data;
      setBil(bilData);
        setFormData({
          internt_nummer: bilData.internt_nummer,
          nummerplade: bilData.nummerplade || '',
          model: bilData.model || '',
          vogn_type: bilData.vogn_type || '',
          status: bilData.status,
          station_id: bilData.station_id || '',
          type: bilData.type,
          boat_model: bilData.boat_model || '',
          boat_motor: bilData.boat_motor || '',
          boat_motor_code: bilData.boat_motor_code || '',
          boat_notes: bilData.boat_notes || ''
        });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bil data:', error);
      setLoading(false);
    }
  };

  const fetchStationer = async () => {
    try {
      const response = await axios.get('/api/stationer');
      setStationer(response.data);
    } catch (error) {
      console.error('Error fetching stationer:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`/api/biler/${id}`, formData);
      navigate(`/biler/profil/${id}`);
    } catch (error) {
      console.error('Error updating bil:', error);
      alert('Der opstod en fejl under opdatering af bilen');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  const normalizedVognType = (formData.vogn_type || '').toLowerCase();
  const isBoatTrailer = ['både trailer', 'båd trailer', 'bådtrailer', 'bad trailer', 'boat trailer']
    .some((entry) => normalizedVognType === entry);

  if (!bil) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Bil ikke fundet</h2>
          <button
            onClick={() => navigate('/biler')}
            className="mt-4 px-4 py-2 bg-falck-blue text-white rounded-lg hover:bg-blue-700"
          >
            Tilbage til biler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/biler/profil/${id}`)}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Rediger bil: {bil.internt_nummer}
            </h1>
            <p className="text-gray-600">Opdater bil information</p>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Grundlæggende Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <FaCar className="text-falck-blue" />
                Grundlæggende Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internt nummer *
                  </label>
                  <input
                    type="text"
                    name="internt_nummer"
                    required
                    value={formData.internt_nummer}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nummerplade
                  </label>
                  <input
                    type="text"
                    name="nummerplade"
                    value={formData.nummerplade}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vogn type
                  </label>
                  <input
                    type="text"
                    name="vogn_type"
                    value={formData.vogn_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {isBoatTrailer && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Båd model</label>
                  <input
                    type="text"
                    name="boat_model"
                    value={formData.boat_model}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Motor</label>
                  <input
                    type="text"
                    name="boat_motor"
                    value={formData.boat_motor}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Motorkode</label>
                  <input
                    type="text"
                    name="boat_motor_code"
                    value={formData.boat_motor_code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Noter</label>
                  <textarea
                    name="boat_notes"
                    value={formData.boat_notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>
            )}

            {/* Status og Type */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Status og Type
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  >
                    <option value="I drift">I drift</option>
                    <option value="På værksted">På værksted</option>
                    <option value="Udlånt">Udlånt</option>
                    <option value="Reserve">Reserve</option>
                    <option value="Til salg">Til salg</option>
                    <option value="Solgt">Solgt</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  >
                    <option value="Almindelig">Almindelig</option>
                    <option value="Lånebil">Lånebil</option>
                      <option value="Bad trailer">Bad trailer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Station
                  </label>
                  <select
                    name="station_id"
                    value={formData.station_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  >
                    <option value="">Vælg station</option>
                    {stationer.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.navn}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/biler/profil/${id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuller
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors ${
                saving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-falck-blue hover:bg-blue-700'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Gemmer...
                </>
              ) : (
                <>
                  <FaSave />
                  Gem ændringer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BilEdit;
